import { useCallback, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { useBusinessLayer } from '@mopla/business-logic';
import { AppEnum } from '@mopla/data-models';
import { MplTrans } from '@mopla/i18';
import { usePersistentCountdownGuard } from '@mopla/utils';

import { MplButton } from '../../../atoms/MplButton/MplButton';
import { VerificationAnim } from '../../../molecules/Animations';
import { useProfileManagerContext } from '../context';

export const VerifyEditEmailScreen = () => {
	const [loading, setLoading] = useState(false);
	const { pendingNewEmail } = useProfileManagerContext();
	const bl = useBusinessLayer();

	const { reinitCountdownGuard, countdownGuardValue } =
		usePersistentCountdownGuard('PersonalDetails_EditEmail_countdownGuard');

	const handleResendEmail = useCallback(async () => {
		try {
			setLoading(true);
			await bl.api.patch('/api/users/email', {
				email: pendingNewEmail,
				origin: AppEnum.PASSENGER,
			});
			reinitCountdownGuard();
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	}, [bl, pendingNewEmail, reinitCountdownGuard]);

	/** We don't show anything until the countdownGuard is not initialized */
	if (countdownGuardValue == null) {
		return null;
	}

	return (
		<Stack
			sx={{
				padding: '16px 24px',
				minHeight: '100%',
				boxSizing: 'border-box',
				justifyContent: 'space-between',
			}}
			direction="column"
		>
			<Box>
				<Box width="100%">
					<VerificationAnim />
				</Box>
				<Typography variant="body2" mt="24px" mb="16px">
					<MplTrans
						ns="profile"
						i18nKey="edit_email.verification_screen.description"
						values={{ email: pendingNewEmail }}
					/>
				</Typography>
				<Typography variant="caption" color="neutral.80">
					<MplTrans
						ns="profile"
						i18nKey="edit_email.verification_screen.caption"
					/>
				</Typography>
			</Box>
			<Box sx={{ justifyItems: 'center', marginBottom: '30px' }}>
				<Typography variant="body2" textAlign="center">
					<MplTrans
						ns="profile"
						i18nKey="edit_email.verification_screen.not_received"
					/>
				</Typography>
				<Stack direction="row" alignItems="center" justifyContent="center">
					<MplButton
						variant="text"
						disabled={!!countdownGuardValue || loading}
						showLoader={loading}
						onClick={handleResendEmail}
					>
						<MplTrans
							ns="profile"
							i18nKey="edit_email.verification_screen.resend"
						/>
					</MplButton>
					{(countdownGuardValue || 0) > 0 && (
						<Typography color="primary">
							<MplTrans
								ns="profile"
								i18nKey="edit_email.verification_screen.timer"
								values={{ time: countdownGuardValue }}
							/>
						</Typography>
					)}
				</Stack>
			</Box>
		</Stack>
	);
};
